import {
    MenuCompositeData,
    MenuExtraGroupData,
    MenuExtraProductData,
    MenuItem,
    MenuItemData,
    MenuPlgData,
    MenuPlgSellableData,
    MenuProductLinePlgQtyData,
    MenuProductLineQtyData,
    MenuQtyData,
    MenuSellableData
} from '../menu/menu-entity.model';
import {MenuItemSelection, MenuItemSize, MenuModel} from '../menu/menu.model';
import {
    CustomerDetails,
    CustomerDetailsService,
    PaymentCustomerDetails
} from '../customer-info/customer-details.service';
import {
    AvailableOrderTimesRequestData,
    AvailableOrderTimesResponseData,
    AvailableOrderTimesService
} from "./available-order-times.service";
import {
    ExtraItemData,
    InitiatedOrderData,
    InitiatedOrderItemData,
    OrderDealData,
    OrderInitiationData,
    OrderInitiationService,
    OrderItemData,
    OrderMataTagName,
    OrderMataTagType,
    OrderMetaTagData,
    PaymentSurchargeData,
    UnavailabilityReason
} from "./order-initiation.service";
import {
    OrderItemSchedulesData,
    OrderPlacementData,
    OrderPlacementService,
    OrderPlacementStatus,
    PaymentAuthorisationData,
    PlacedOrderData,
    PlacedOrdersData
} from "./order-placement.service";
import {GroupOrderModel} from '../group-order/group-order.model';
import {
    OrderTransferRequestPollService,
    OrderTransferRequestsData,
    OrderTransferTargetStatus
} from '../group-order/order-transfer-request-poll.service';
import {OrderTransferTargetReferenceData} from '../group-order/order-transfer-target-entity.model';
import {OrderTransferService} from '../group-order/order-transfer.service';
import {computed, effect, inject, Injectable, Injector, Signal, signal, untracked, WritableSignal} from "@angular/core";
import {debounceTime, Subject} from "rxjs";
import {onEach} from '../utils/observable-utils';
import {OrderingPhase, SessionModel, SessionReset} from "../session/session.model";
import {ResponsiveService} from "../responsive/responsive.service";
import {NavigationService} from "../navigation/navigation.service";
import {
    DataClientError,
    DataServerConditionNotification,
    DisplayableDataServerMessage,
    EntityData
} from "@fedelta-pos/data-client";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {formatDateForDisplay, formatTimeForDisplay} from '../utils/date-time-utils';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {IntegratedPaymentProvider, PaymentCard, PaymentType, ThreeDSResult} from '../payment/payment';
import {PaymentModel} from '../payment/payment.model';
import {TapToPayService} from '../payment/tap-to-pay/tap-to-pay.service';
import {LoadingService} from "../ui-elements/loading/loading.service";
import {AppUserService} from '../identity/app-user.service';
import {IdentityModel} from '../identity/identity.model';
import {PendingOrderData, PendingOrdersData, PendingOrdersService} from "./pending-orders.service";
import {OrderPostscriptData, OrderPostscriptService} from "./order-postscript.service";
import {OrderPlacementStatusData, OrderPlacementStatusService} from "./order-placement-status.service";
import {RecentOrderData, RecentOrderService} from "./recent-order.service";
import {DealData} from '../deal/deal-entity.model';
import {DealModel} from '../deal/deal.model';
import {DisplayableOrderItem, OrderCompositeItem, OrderExtraItem, OrderItem, OrderPlgItem, OrderType} from './order';
import {AnalyticsService, OrderPlacedEvent} from '../logging/analytics.service';
import {PossessivePipe} from '../utils/possessive.pipe';
import {uuid} from '../utils/string-utils';
import {ConfigurationModel} from '../configuration/configuration.model';
import {whenSignalMatches} from '../utils/signal-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {LoadingStatus} from '../session/loading-status';

export class OrderErrorStates {
    public readonly orderWithInvalidTimeAttempted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly tableOrderWithTimeAttempted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly emptyOrderAttempted: WritableSignal<boolean> = signal<boolean>(false);
    public readonly minMaxOrderAmountAttempted: WritableSignal<boolean> = signal<boolean>(false);

    public readonly hasErrors: Signal<boolean> = computed<boolean>(() =>
        this.emptyOrderAttempted() ||
        this.orderWithInvalidTimeAttempted() ||
        this.minMaxOrderAmountAttempted());

    public clearOrderingContextErrors(): void {
        this.orderWithInvalidTimeAttempted.set(false);
        this.tableOrderWithTimeAttempted.set(false);
    }

    public clearOrderLimitErrors(): void {
        this.emptyOrderAttempted.set(false);
        this.minMaxOrderAmountAttempted.set(false);
    }
}

export enum OrderErrorCode {
    ORDER_NOT_INITIATED,
    ORDER_TOKEN_MISSING
}

export class OrderError {
    constructor(public code: OrderErrorCode, public message: string) {
    }
}

export class TransferRequest {
    items: OrderItem[];
    subTotal: number;

    constructor(public transferRequestId: string, public transferRequestName: string) {
    }
}

export class UnavailableOrderItemRemovalRequest {
    existence?: boolean;
    orderType?: OrderType;
    schedules?: boolean;
    confirm?: boolean;
}

export enum OrderInitiationStatus {
    UNINITIATED,
    INITIATING,
    INITIATED,
    FAILED
}

export enum OrderSubmissionStatus {
    SUBMITTING,
    COMPLETE,
    INCOMPLETE,
    ORDER_INVALID,
    ORDER_CANCELLED
}

@Injectable({providedIn: "root"})
export class OrderModel implements DeferredInjectable {
    private injector: Injector = inject(Injector);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private pendingOrdersService: PendingOrdersService = inject(PendingOrdersService);
    private menuModel: MenuModel = inject(MenuModel);
    private dealModel: DealModel = inject(DealModel);
    private tapToPayService: TapToPayService = inject(TapToPayService);
    private paymentModel: PaymentModel = inject(PaymentModel);
    private recentOrderService: RecentOrderService = inject(RecentOrderService);
    private orderInitiationService: OrderInitiationService = inject(OrderInitiationService);
    private orderPlacementService: OrderPlacementService = inject(OrderPlacementService);
    private orderPlacementStatusService: OrderPlacementStatusService = inject(OrderPlacementStatusService);
    private orderPostscriptService: OrderPostscriptService = inject(OrderPostscriptService);
    private orderTransferService: OrderTransferService = inject(OrderTransferService);
    private orderTransferRequestPollService: OrderTransferRequestPollService = inject(OrderTransferRequestPollService);
    private availableOrderTimesService: AvailableOrderTimesService = inject(AvailableOrderTimesService);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    private appUserService: AppUserService = inject(AppUserService);
    private notificationService: NotificationService = inject(NotificationService);
    private navigationService: NavigationService = inject(NavigationService);
    private loadingService: LoadingService = inject(LoadingService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private groupOrderModel: GroupOrderModel;

    public static instance: OrderModel;

    private static readonly ORDER_INITIATION_ATTEMPTS_LIMIT: number = 3;
    private static readonly ORDER_PLACEMENT_DELAY_TRIGGER_SECONDS: number = 3;
    private static readonly ORDER_PLACEMENT_AUTO_RETRY_WINDOW_MS: number = 60 * 1000;

    public static readonly SHOW_RELATIVE_PRICING_FOR_SIZES: boolean = false;
    public static readonly SHOW_RELATIVE_PRICING_FOR_EXTRAS: boolean = false;
    public static readonly SHOW_RELATIVE_PRICING_FOR_EXTRA_SIZES: boolean = false;

    private static readonly REORDER_ROUND_CUTOFF_HOURS: number = 8;

    // The order

    private readonly _orderItems: WritableSignal<OrderItem[]> = signal<OrderItem[]>([]);
    // noinspection JSUnusedGlobalSymbols
    public readonly orderItems: Signal<OrderItem[]> = this._orderItems.asReadonly();
    public readonly orderHasItems: Signal<boolean> = computed<boolean>(() => this.isOrderValid() && this._orderItems().length > 0);
    public readonly recentlyOrderedItems: Signal<OrderItem[]> = computed<OrderItem[]>(() =>
        this._orderItems().filter((item: OrderItem) => item.justOrdered() && !item.transferRequestId));
    public readonly personalOrderStarted: Signal<boolean> = computed<boolean>(() => this.dealModel.appliedDeals().length > 0 || this.hasOrderedAnyItemPersonally());
    public readonly orderedSellableIds: Signal<number[]> = computed<number[]>(() => this._orderItems().flatMap((item: OrderItem) =>
        [item.sellable().sellableId, ...(item.compositeComponentsSignal()?.map((component: OrderItem) => component.sellable().sellableId) ?? [])]));
    private readonly _orderNotes: WritableSignal<string> = signal<string>(undefined);
    public readonly orderNotes: Signal<string> = this._orderNotes.asReadonly();
    private readonly _orderErrorStates: WritableSignal<OrderErrorStates> = signal<OrderErrorStates>(new OrderErrorStates());
    public readonly orderErrorStates: Signal<OrderErrorStates> = this._orderErrorStates.asReadonly();
    public readonly totalItems: Signal<number> = computed<number>(() =>
        this._orderItems().reduce((total: number, item: OrderItem) => total + item.quantitySignal(), 0));
    public readonly tipAmount: WritableSignal<number> = signal<number>(0);
    public readonly isOrderValid: WritableSignal<boolean> = signal<boolean>(false);

    // Tracks whether the user has attempted to add the current item being ordered to the order.
    // This is intended to control whether certain messages are displayed as errors.
    public readonly addToOrderAttempted: WritableSignal<boolean> = signal<boolean>(false);
    private readonly hasOrderBeenPersisted: WritableSignal<boolean> = signal<boolean>(false);

    public readonly transferRequests: Signal<TransferRequest[]> = computed<TransferRequest[]>(() => {
        const items: OrderItem[] = this._orderItems();
        return items
            .map((item: OrderItem) => new TransferRequest(item.transferRequestId, item.transferRequestName))
            .filter((request: TransferRequest, index: number, requests: TransferRequest[]) => requests.findIndex((r: TransferRequest) => r.transferRequestId === request.transferRequestId) === index)
            .map((request: TransferRequest) => {
                request.items = items.filter((item: OrderItem) => item.transferRequestId == request.transferRequestId);
                request.subTotal = request.items.reduce((total: number, item: OrderItem) => total + item.initiatedTotalSignal(), 0);
                return request;
            })
            .sort((a: TransferRequest, b: TransferRequest) => (a.transferRequestName ?? "").localeCompare(b.transferRequestName ?? ""));
    });
    public readonly totalTransfers: Signal<number> = computed(() => this.transferRequests().filter((request: TransferRequest) => request.transferRequestId).length);

    public readonly orderStarted: Signal<boolean> = computed<boolean>(() => this.dealModel.appliedDeals().length > 0 || this.orderHasItems());

    public readonly isOrderReadyForPayment: Signal<boolean> = computed<boolean>(() =>
        this.isOrderValid() && this._orderInitiationStatus() !== OrderInitiationStatus.UNINITIATED && this._orderInitiationStatus() !== OrderInitiationStatus.FAILED);

    public readonly canPersistOrderDetails: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.isConsumerActor() && (!this.identityModel.isGuestUser() || this.responsiveService.isMobile() || this.configurationModel.devOrDemoMode()));

    public readonly menuItemAtLimit: Signal<(menuItem: MenuItem, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean> = computed(() => {
        const isOrderValid: boolean = this.isOrderValid();
        const qtyOrderedPerSellableId: Map<number, number> = this.qtyOrderedPerSellableId();
        const sellableQtyAvailable: (sellableId: number) => number = this.menuModel.sellableQtyAvailable();
        const menuItemAtLimit = (menuItem: MenuItem, quantum: number = 1, qtyPending: number = 0, excludedQty: number = 0): boolean => {
            const qtyToAdd: number = quantum + qtyPending;
            if (menuItem && menuItem.sellable) {
                const qtyAvailable: number = sellableQtyAvailable(menuItem.sellable.sellableId);
                if (qtyAvailable != undefined && isOrderValid && (qtyOrderedPerSellableId.get(menuItem.sellable.sellableId) ?? 0) - excludedQty + qtyToAdd > qtyAvailable) {
                    return true;
                }
            }
            if (this.menuModel.isNormalMenuItem(menuItem)) {
                this.menuModel.initialiseItem((<MenuItemData>menuItem));
                if ((<MenuItemData>menuItem).sellable.composite &&
                    (<MenuItemData>menuItem).sellable.composite.prodLineQuantities.some((prodLineQty: MenuProductLineQtyData) => menuItemAtLimit(prodLineQty.productLine.menuItem, qtyToAdd, 0, excludedQty))) {
                    return true;
                }
            }
            return false;
        };
        return menuItemAtLimit;
    });

    public readonly allSizesAtLimit: Signal<(menuItem: MenuItemData, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean> = computed(() => {
        const menuItemAtLimit: (menuItem: MenuItem, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean = this.menuItemAtLimit();
        const itemSizes: (menuItem: MenuItemData) => MenuItemSize[] = this.menuModel.itemSizes();
        return (menuItem: MenuItemData, quantum: number = 1, qtyPending: number = 0, excludedQty: number = 0): boolean => {
            const sizes: MenuItemSize[] = itemSizes(menuItem);
            return sizes && sizes.length > 1
                ? sizes.every((size: MenuItemSize) => menuItemAtLimit(size.menuItem, quantum, qtyPending, excludedQty))
                : menuItemAtLimit(menuItem, quantum, qtyPending, excludedQty);
        };
    });

    public readonly sellableAtLimit: Signal<(sellableId: number, quantum?: number, qtyPending?: number, excludedQty?: number) => boolean> = computed(() => {
        const isOrderValid: boolean = this.isOrderValid();
        const sellableQtyAvailable: (sellableId: number) => number = this.menuModel.sellableQtyAvailable();
        const qtyOrderedPerSellableId: Map<number, number> = this.qtyOrderedPerSellableId();
        return (sellableId: number, quantum: number = 1, qtyPending: number = 0, excludedQty: number = 0): boolean => {
            const qtyToAdd: number = quantum + qtyPending;
            if (sellableId) {
                const qtyAvailable: number = sellableQtyAvailable(sellableId);
                if (qtyAvailable != undefined && isOrderValid && (qtyOrderedPerSellableId.get(sellableId) ?? 0) - excludedQty + qtyToAdd > qtyAvailable) {
                    return true;
                }
            }
            return false;
        };
    });

    public readonly personallyOrderedItems: Signal<Set<OrderItem>> = computed<Set<OrderItem>>(() =>
        this.isOrderValid()
            ? new Set(this._orderItems().filter((item: OrderItem) => !item.transferRequestId && item.sellable()))
            : new Set<OrderItem>());

    public readonly hasOrderedAnyItemPersonally: Signal<boolean> = computed<boolean>(() => !!this.personallyOrderedItems().size);

    public readonly personallyOrderedSellables: Signal<Set<MenuSellableData>> = computed<Set<MenuSellableData>>(() =>
        new Set([...this.personallyOrderedItems()].map((item: OrderItem) => item.sellable())));

    public readonly personallyJustOrderedSellables: Signal<Set<MenuSellableData>> = computed<Set<MenuSellableData>>(() =>
        new Set([...this.personallyOrderedItems()].filter((item: OrderItem) => item.justOrdered()).map((item: OrderItem) => item.sellable())));

    public readonly personallyOrderedSellableIds: Signal<Set<number>> = computed<Set<number>>(() =>
        new Set([...this.personallyOrderedItems()].map((item: OrderItem) => item.sellable().sellableId)));

    // Order initiation
    private readonly _orderInitiationStatus: WritableSignal<OrderInitiationStatus> = signal(OrderInitiationStatus.UNINITIATED);
    public readonly orderInitiationStatus: Signal<OrderInitiationStatus> = this._orderInitiationStatus.asReadonly();
    private readonly orderInitiation: WritableSignal<OrderInitiationData> = signal<OrderInitiationData>(<OrderInitiationData>EntityData.create(OrderInitiationData));
    private readonly _initiatedOrder: WritableSignal<InitiatedOrderData> = signal<InitiatedOrderData>(<InitiatedOrderData>EntityData.create(InitiatedOrderData));
    public readonly initiatedOrder: Signal<InitiatedOrderData> = this._initiatedOrder.asReadonly();
    private readonly _orderInitiatedTime: WritableSignal<number> = signal<number>(0);
    public readonly orderInitiatedTime: Signal<number> = this._orderInitiatedTime.asReadonly();
    private readonly _initiationErrorKey: WritableSignal<string> = signal<string>(undefined);
    public readonly initiationErrorKey: Signal<string> = this._initiationErrorKey.asReadonly();
    private readonly _initiationErrorParams: WritableSignal<Record<string, string | number>> = signal<Record<string, string | number>>(undefined);
    public readonly initiationErrorParams: Signal<Record<string, string | number>> = this._initiationErrorParams.asReadonly();

    public readonly formattedEarliestTime: Signal<string> = computed(() =>
        this.initiatedOrder().earliestLocalTime ? this.configurationModel.relativeDateAndTime()(formatDateForDisplay(this.initiatedOrder().earliestDate, false), this.initiatedOrder().earliestLocalTime) : undefined);

    public readonly storeUnavailableDueToClosure: Signal<boolean> = computed(() =>
        this._initiatedOrder()?.cannotAllocateOrderLaterDateTime &&
        ([UnavailabilityReason.UNAVAILABLE_CLOSED, UnavailabilityReason.UNAVAILABLE_CLOSED_FOR_DAY, UnavailabilityReason.UNAVAILABLE_CLOSING].includes(this._initiatedOrder().unavailabilityReason)));

    public readonly orderTimeInitiationPending: Signal<boolean> = computed(() =>
        this._orderInitiationStatus() === OrderInitiationStatus.INITIATING ||
        this.orderInitiation().requestId !== this.initiatedOrder().requestId ||
        (this.orderingContextModel.expectedCompletionAsString() && this.orderingContextModel.expectedCompletionAsString() !== this.orderInitiation().expectedCompletion));

    public readonly showOrderTimeAllocationNotice: Signal<boolean> = computed<boolean>(() =>
        !!this._initiatedOrder()?.cannotAllocateOrderLaterDateTime &&
        (!this.storeUnavailableDueToClosure() ||
            this.orderingContextModel.canShowAsapSelectionTime() ||
            (!this._initiatedOrder().earliestLocalTime && !this.orderTimeInitiationPending())));

    public readonly orderTimeAllocationNotice: Signal<string> = computed(() =>
        !this._initiatedOrder() || !this.showOrderTimeAllocationNotice() ? undefined
            : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CLOSED ? (this.orderingContextModel.expectedCompletion() ? "woa.message.order-time.storeClosedAtTime" : "woa.message.order-time.storeClosedNow")
                : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CLOSED_FOR_DAY ? "woa.message.order-time.storeClosedForTheDay"
                    : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_CLOSING ? "woa.message.order-time.storeClosing"
                        : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_OFFLINE ? "woa.message.order-time.storeOffline"
                            : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_PAST ? "woa.message.order-time.timeInPast"
                                : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_TOO_FAR_AHEAD ? "woa.message.order-time.tooFarAhead"
                                    : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_LEAD_TIME ? "woa.message.order-time.leadTime"
                                        : this._initiatedOrder().unavailabilityReason === UnavailabilityReason.INTEGRATED_DELIVERY_NOT_AVAILABLE ? "woa.message.order-time.integratedDeliveryNotAvailable"
                                            : "woa.message.order-time.cannotAllocate");

    public readonly invalidOrderTimeMessage: Signal<string> = computed(() =>
        this._initiatedOrder().unavailabilityReason === UnavailabilityReason.UNAVAILABLE_OFFLINE ? "woa.error.order.storeOffline"
            : this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE ? "woa.error.order.storeUnavailableForTableOrders"
                : !this.orderingContextModel.canShowAsapSelectionTime() && !this.orderingContextModel.expectedCompletion() ? "woa.error.order.noTime"
                    : "woa.error.order.invalidTime");

    private readonly estimatedCompletionLocalDateTime: Signal<string> = computed(() =>
        this._initiatedOrder() ? this._initiatedOrder().estimatedCompletionLocalDateTime : undefined);

    public readonly orderEta: Signal<string> = computed(() =>
        this.orderingContextModel.expectedCompletion() || !this.estimatedCompletionLocalDateTime() || this.showOrderTimeAllocationNotice() || !this.orderingContextModel.canShowAsapSelectionTime() ||
        (this.orderingContextModel.integratedDelivery() && (this.sessionModel.orderingPhase() === OrderingPhase.ORDERING || !this.orderingContextModel.address())) ? undefined
            : formatTimeForDisplay(EntityData.parseLocalDateTime(this.estimatedCompletionLocalDateTime())));

    public readonly orderTotal: Signal<number> = computed<number>(() =>
        this._initiatedOrder() ? this._initiatedOrder().getOrderOrderTotalWithPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE) : 0);

    public readonly orderTotalPayment: Signal<number> = computed<number>(() => this.orderTotal() + (this.tipAmount() || 0));

    public readonly paymentRequired: Signal<boolean> = computed<boolean>(() => this.orderTotalPayment() > 0);

    public readonly timeSelectionRequired: Signal<boolean> = computed<boolean>(() => this.orderingContextModel.orderType() !== OrderType.ORDER_AT_TABLE && !this.orderingContextModel.canShowAsapSelectionTime() && !this.orderingContextModel.expectedCompletion());

    public readonly showMinOrderAheadAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().minPickupOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) < this.orderingContextModel.currentStore().minPickupOrderAmount);

    public readonly showMaxOrderAheadAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.orderType() === OrderType.ORDER_AHEAD && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().maxPickupOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) > this.orderingContextModel.currentStore().maxPickupOrderAmount);

    public readonly showMinDeliveryAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.isConfirmedDeliveryOrdering() && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().minDeliveryOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) < this.orderingContextModel.currentStore().minDeliveryOrderAmount);

    public readonly showMaxDeliveryAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.isConfirmedDeliveryOrdering() && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().maxDeliveryOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) > this.orderingContextModel.currentStore().maxDeliveryOrderAmount);

    public readonly showMinOrderAtTableAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.isConfirmedTableOrdering() && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().minTableOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) < this.orderingContextModel.currentStore().minTableOrderAmount);

    public readonly showMaxOrderAtTableAmountWarning: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.isConfirmedTableOrdering() && this.orderingContextModel.currentStore() && this.orderingContextModel.currentStore().maxTableOrderAmount &&
        this._initiatedOrder() && (this.orderTotal() || 0) > this.orderingContextModel.currentStore().maxTableOrderAmount);

    public readonly canInitiateOrder: Signal<boolean> = computed<boolean>(() =>
        (this.orderHasItems() || !!this.orderingContextModel.currentStore()) && !!this.orderingContextModel.orderType());

    public readonly orderSaving: Signal<number> = computed<number>(() =>
        this._initiatedOrder().undiscountedOrderTotal ? this._initiatedOrder().undiscountedOrderTotal - this._initiatedOrder().orderTotal : 0);

    public readonly orderTypeChangedSinceLastInitiation: Signal<boolean> = computed<boolean>(() => {
        const initiatedOrderType: OrderType = this.orderInitiation() ? this.orderInitiation().orderTypeEnum : OrderType.NONE;
        const currentOrderType: OrderType = this.orderingContextModel.orderType() ?? OrderType.NONE;
        return initiatedOrderType !== currentOrderType;
    });

    public readonly qtyOrderedPerSellableId: Signal<Map<number, number>> = computed(() => {
        const map: Map<number, number> = new Map();
        this._orderItems().forEach((orderItem: OrderItem) => {
            orderItem.qtyOrderedPerSellableId().forEach((qty: number, sellableId: number) =>
                map.set(sellableId, (map.get(sellableId) ?? 0) + qty));
        });
        return map;
    });

    /**
     * Requests ordering of the specified item.
     */
    public readonly requestOrderItem$: Subject<MenuItemSelection> = new Subject();

    private readonly orderInitiationRequested$: Subject<void> = new Subject<void>();
    private initiationSequence: number = 0;

    // Order placement
    public readonly orderSubmissionStatus: WritableSignal<OrderSubmissionStatus> = signal(undefined);
    private readonly orderPlacement: WritableSignal<OrderPlacementData> = signal<OrderPlacementData>(undefined);
    private readonly orderPlacementStarted: WritableSignal<number> = signal<number>(undefined);
    public readonly orderPlacementFinished: WritableSignal<number> = signal<number>(undefined);
    private readonly _placedOrder: WritableSignal<PlacedOrderData> = signal<PlacedOrderData>(undefined);
    public readonly placedOrder: Signal<PlacedOrderData> = this._placedOrder.asReadonly();
    private readonly removalOfOversoldItemsReported: WritableSignal<boolean> = signal<boolean>(false);

    public readonly hasOrderPlacement: Signal<boolean> = computed<boolean>(() => !!this.orderPlacement()?.requestId);

    private _canReorderOnLoad: boolean = true;
    private _canRestoreOnLoad: boolean = true;

    private verifyingPendingOrders: boolean = false;
    private verifiedOrders: RecentOrderData[] = [];

    private rsaVerified: boolean = false;

    constructor() {
        console.log("--- OrderModel");

        OrderModel.instance = this;

        onEach(this.orderInitiationRequested$.pipe(
            debounceTime(500)
        ), () => this.initiateOrder());

        onEach(this.orderingContextModel.storeDetailsChanged$, () => {
            if (this.configurationModel.isWaiterActor) {
                this.menuModel.rsaBlocked.set(false);
                this.rsaVerified = false;
            }
        });

        onEach(this.sessionModel.sessionReset$, (sessionRest: SessionReset) => {
            this.reset("session reset", sessionRest.retainTable);
            this.hasOrderBeenPersisted.set(false);
        });

        effect(() => {
            if (this.menuModel.currentMenu() && this.isOrderValid()) {
                void untracked(() => {
                    void this.removeUnavailableOrderItems({
                        existence: true,
                        confirm: false
                    });
                    void this.removeOversoldItems();
                });
            }
        });
    }

    public inject(): void {
        this.groupOrderModel = inject(GroupOrderModel);
    }

    public notifyOrderChange(): void {
        this._orderErrorStates().clearOrderLimitErrors();
        this.requestOrderInitiation("order changed");
    }

    private startNewOrder(reason: string): void {
        console.log(Date.now(), "starting new order:", reason);
        this.isOrderValid.set(true);
        this.notifyOrderChange();
    }

    public invalidateOrder(): void {
        this.dealModel.removeAllDeals();
        if (this.orderHasItems()) {
            this.isOrderValid.set(false);
            this._orderItems.set([]);
        }
        this.notifyOrderChange();
    }

    public reset(reason: string, retainRsaVerification: boolean): void {
        console.log(Date.now(), "resetting order model:", reason);
        this.isOrderValid.set(false);
        this._orderItems.set([]);
        this._orderNotes.set(undefined);
        this.tipAmount.set(0);
        this._orderInitiationStatus.set(OrderInitiationStatus.UNINITIATED);
        this._initiationErrorKey.set(undefined);
        this.orderInitiation.set(<OrderInitiationData>EntityData.create(OrderInitiationData));
        this._initiatedOrder.set(<InitiatedOrderData>EntityData.create(InitiatedOrderData));
        this.orderPlacement.set(undefined);
        this._placedOrder.set(undefined);
        this._orderErrorStates.set(new OrderErrorStates());
        if (this.configurationModel.isWaiterActor && !retainRsaVerification) {
            this.rsaVerified = false;
        }
        this.notifyOrderChange();
    }

    public async verifyRsa(): Promise<boolean> {
        return this.rsaVerified ||= ((this.configurationModel.isWaiterActor() && !this.menuModel.rsaBlocked()) ||
            !!await this.navigationService.createPage("woa-rsa-verification-page"));
    }

    public async editNotes(): Promise<boolean> {
        if (this._orderNotes()) {
            void this.analyticsService.trackOrderNotesEdited();
        }
        const result: {
            notes: string
        } = await this.navigationService.createPage("woa-order-notes-edit-page", {notes: this._orderNotes()});
        if (result) {
            this._orderNotes.set(result.notes);
            return true;
        }
        return false;
    }

    public removeNotes(): void {
        void this.analyticsService.trackOrderNotesRemoved();
        this._orderNotes.set(undefined);
    }

    public createOrderItem(menuItem: MenuItemData, quantity: number, orderDefaultExtras: boolean = true): OrderItem {
        if (this.menuModel.currentMenu()) {
            this.menuModel.initialiseItem(menuItem);
        }
        let orderItem: OrderItem;
        if (menuItem.sellable.composite && menuItem.sellable.composite.sellableId) {
            const components: OrderItem[] = [];
            const qtyBeingOrderedPerSellableId: Map<number, number> = new Map();
            menuItem.sellable.composite.prodLineQuantities.forEach((prodLineQty: MenuProductLineQtyData) => {
                components[prodLineQty.indexOrder] = new OrderItem(undefined, prodLineQty.productLine.menuItem, prodLineQty.productLine, this.menuModel.getMenuItemDisplayName(prodLineQty.productLine.menuItem) || prodLineQty.productLine.name, 1, undefined, undefined, undefined, undefined, false);
                qtyBeingOrderedPerSellableId.set(prodLineQty.productLine.sellableId, (qtyBeingOrderedPerSellableId.get(prodLineQty.productLine.sellableId) ?? 0) + 1);
            });
            menuItem.sellable.composite.prodLinePlgQuantities.forEach((prodLinePlgQty: MenuProductLinePlgQtyData) => {
                const defaultPlgSellable: MenuPlgSellableData = prodLinePlgQty.plg.sellableLines.find((plgSellable: MenuPlgSellableData) => plgSellable.defaultSellable);
                let defaultSellable: MenuSellableData = defaultPlgSellable ? defaultPlgSellable.sellable : undefined;

                // Don't set the default if the sellable is already at the available limit
                if (defaultPlgSellable && defaultPlgSellable.sellable) {
                    if (!this.menuModel.plgItemIsAvailable()(defaultPlgSellable.sellable.menuItem, menuItem)) {
                        defaultSellable = undefined;
                    } else if (this.menuModel.sellableQtyAvailable()(defaultPlgSellable.sellable.sellableId) != undefined) {
                        const qtyOrdered: number = (this.qtyOrderedPerSellableId().get(defaultSellable.sellableId) ?? 0) + (qtyBeingOrderedPerSellableId.get(defaultSellable.sellableId) ?? 0);
                        if (qtyOrdered >= this.menuModel.sellableQtyAvailable()(defaultPlgSellable.sellable.sellableId)) {
                            defaultSellable = undefined;
                        }
                    }
                }

                components[prodLinePlgQty.indexOrder] = new OrderPlgItem(undefined, prodLinePlgQty.plg, defaultSellable ? (this.menuModel.getMenuItemDisplayName(defaultSellable.menuItem) || defaultSellable.name) : undefined, defaultSellable, 1);
            });
            const orderCompositeItem: OrderCompositeItem = new OrderCompositeItem(menuItem.sellable.composite, this.menuModel.getMenuItemDisplayName(menuItem), menuItem, menuItem.sellable, quantity, components);
            orderItem = orderCompositeItem;

            // Extras are added last so that available-quantity checks take into account order line quantities.
            if (orderDefaultExtras) {
                orderCompositeItem.compositeComponentsSignal().forEach((compositeOrderItem: OrderItem) => compositeOrderItem.addDefaultExtras(orderCompositeItem));
            }
        } else {
            orderItem = new OrderItem(undefined, menuItem, menuItem.sellable, this.menuModel.getMenuItemDisplayName(menuItem), quantity, undefined, undefined, undefined, undefined, false);

            if (orderDefaultExtras) {
                orderItem.addDefaultExtras();
            }
        }
        return orderItem;
    }

    public addOrderItem(orderItem: OrderItem, index?: number): void {
        if (!this.isOrderValid()) {
            this.startNewOrder("new item");
        }
        orderItem.addToList(this._orderItems, index);
        this.notifyOrderChange();
    }

    public addOrderItems(orderItems: OrderItem[]): void {
        if (!this.isOrderValid()) {
            this.startNewOrder("new items");
        }
        let changed: boolean = false;
        orderItems.forEach((orderItem: OrderItem) => {
            if (orderItem) {
                orderItem.addToList(this._orderItems);
                changed = true;
            }
        });
        if (changed) {
            this.notifyOrderChange();
        }
    }

    public async removeOrderItem(orderItem: DisplayableOrderItem): Promise<boolean> {
        const deal: DealData = orderItem.dealIdSignal() ? this.dealModel.findAppliedDealById()(orderItem.dealIdSignal()) : undefined;
        if (deal) {
            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveDeal", undefined,
                "woa.message.order.confirmRemoveItemAndDeal",
                "woa.button.common.noThanks", "woa.button.common.remove", true,
                {
                    item: orderItem.nameSignal(),
                    dealName: this.dealModel.getDealName(deal)
                })) {
                return false;
            }
            this.dealModel.removeDeal(deal);
        } else {
            this.removeOrderItemByIdentifier(orderItem.identifier);
        }

        void this.analyticsService.trackItemRemoved(orderItem);
        return true;
    }

    private removeOrderItemByIdentifier(identifier: string, notify: boolean = true): boolean {
        const index: number = this._orderItems().findIndex((item: OrderItem) => item.identifier === identifier);
        if (index < 0) {
            return false;
        }
        this._orderItems.update(items => items.toSpliced(index, 1));
        if (!this._orderItems().length) {
            this.orderInitiation.set(<OrderInitiationData>EntityData.create(OrderInitiationData));
            this._initiatedOrder.set(<InitiatedOrderData>EntityData.create(InitiatedOrderData));
            this._orderInitiationStatus.set(OrderInitiationStatus.UNINITIATED);
        }
        if (notify) {
            this.notifyOrderChange();
        }
        return true;
    }

    public removeOrderItemsByIdentifier(identifiers: string[]): boolean {
        let removed: boolean = false;
        identifiers.forEach((identifier) => {
            if (this.removeOrderItemByIdentifier(identifier, false)) {
                removed = true;
            }
        });
        this.notifyOrderChange();
        return removed;
    }

    public removeTransferRequestOrderItems(predicate?: TransferRequest | ((transferRequestId: string) => boolean)): boolean {
        return this.removeOrderItemsByIdentifier(this._orderItems()
            .filter((orderItem: OrderItem) => orderItem.transferRequestId && (!predicate ||
                (predicate instanceof TransferRequest
                    ? orderItem.transferRequestId === predicate.transferRequestId
                    : (<(transferRequestId: string) => boolean>predicate)(orderItem.transferRequestId))))
            .map(((orderItem: OrderItem) => orderItem.identifier)));
    }

    public replaceOrderItemByIdentifier(identifier: string, orderItem: OrderItem): void {
        const index: number = this._orderItems().findIndex((item: OrderItem) => item.identifier === identifier);
        this.removeOrderItemByIdentifier(identifier, false);
        this.addOrderItem(orderItem, index);
    }

    /**
     * Attempts to restore details of the locally persisted current order.
     * The current order must be empty.
     * Details which are restored include customer contact details, and ordered items.
     * Customer contact details are assumed to be untrusted, and saved customer details will be used in preference if available.
     *
     * Returns true if the order exists and has ordered items.
     */
    public async restoreOrder(): Promise<boolean> {
        if (this.orderHasItems() || !this.menuModel.currentMenu() || !this.orderingContextModel.currentStore()) {
            return false;
        }

        const recentOrder: RecentOrderData = this.recentOrderService.getCurrentOrder();
        if (!recentOrder) {
            return false;
        }

        this._orderNotes.set(recentOrder.orderNotes);
        this.orderingContextModel.firstName.set(recentOrder.orderInitiation.firstName === "NOT ASSIGNED" ? undefined : recentOrder.orderInitiation.firstName);
        this.orderingContextModel.lastName.set(recentOrder.orderInitiation.lastName === "NOT ASSIGNED" ? undefined : recentOrder.orderInitiation.lastName);
        this.orderingContextModel.email.set(recentOrder.orderInitiation.email);
        this.orderingContextModel.mobilePhone.set(recentOrder.orderInitiation.orderPhoneNumber);
        this.orderingContextModel.address.set(recentOrder.orderInitiation.address);
        this.orderingContextModel.deliveryInstructions.set(recentOrder.orderInitiation.deliveryInstructions);
        this.orderingContextModel.firstNameTrusted.set(false);
        this.orderingContextModel.lastNameTrusted.set(false);
        this.orderingContextModel.emailTrusted.set(false);
        this.orderingContextModel.mobilePhoneTrusted.set(false);
        this.orderingContextModel.addressTrusted.set(false);
        this.orderingContextModel.deliveryInstructionsTrusted.set(false);

        // Reload any saved customer details and/or reapply member details
        this.orderingContextModel.applyIdentityContactDetails(this.identityModel.currentIdentity());

        if (recentOrder.orderInitiation.items.length) {
            this.addOrderItems(this.recreateOrderItems(recentOrder.orderInitiation.items, true, true, true));
        }

        if (!this.orderingContextModel.orderType()) {
            await this.orderingContextModel.applyOrderType(recentOrder.orderInitiation.orderTypeEnum);
            this.requestOrderInitiation("restore order", true);
        }

        // Attempt to Recreate or update the group order that was current when the order was initiated
        if (recentOrder.orderInitiation.orderTransferTargetId && !this.groupOrderModel.hasGroupOrder() && this.groupOrderModel.canHaveGroupOrder()) {
            await this.groupOrderModel.saveGroupOrder(true, recentOrder.orderInitiation.transferTargetName, recentOrder.orderInitiation.orderTransferTargetId);
        }

        // Apply the deals that were attached to the order
        if (recentOrder.deals) {
            recentOrder.deals.forEach((deal: DealData) => {
                if (recentOrder.orderInitiation.deals.some((orderDeal: OrderDealData) => orderDeal.id === deal.id)) {
                    this.dealModel.applyDeal(deal);
                }
            });
        }

        return this.orderHasItems();
    }

    private recreateOrderItems(items: OrderItemData[], checkQtyAvailable: boolean = true, ignoreProposed: boolean = false, restored: boolean = false): OrderItem[] {
        const runningTotal: Map<number, number> = new Map();
        return items
            .filter((orderItemData: OrderItemData) => !(ignoreProposed && orderItemData.proposed))
            .map((orderItemData: OrderItemData) => [orderItemData, this.menuModel.findMergedItemForSellable()({
                sellableId: orderItemData.sellableId,
                orderType: this.orderingContextModel.orderType()
            })])
            .filter(([, menuItem]: [OrderItemData, MenuItemData]) => this.menuModel.itemIsAvailable()(menuItem))
            .map(([orderItemData, menuItem]: [OrderItemData, MenuItemData]) => orderItemData.compositeComponentsSignal().length
                ? this.createOrderCompositeItemFromOrderItemData(orderItemData, menuItem, checkQtyAvailable, runningTotal)
                : this.createOrderItemFromOrderItemData(orderItemData, menuItem, undefined, checkQtyAvailable, undefined, runningTotal))
            .filter((orderItem: OrderItem) => !!orderItem)
            .map((orderItem: OrderItem) => {
                if (restored) {
                    // This will prevent the item from being marked as justOrdered when the order initiation completes
                    orderItem.initiatedTotalSignal.set(0);
                }
                return orderItem;
            });
    }

    private sellableQtyStillAvailable(sellableId: number, runningTotal: Map<number, number>): number {
        return this.menuModel.sellableQtyAvailable()(sellableId) - (this.qtyOrderedPerSellableId().get(sellableId) ?? 0) - (runningTotal?.get(sellableId) ?? 0);
    }

    private addExtrasFromOrderItemData(orderItem: OrderItem,
                                       orderItemData: OrderItemData,
                                       checkQtyAvailable: boolean,
                                       parentQty: number,
                                       runningTotal: Map<number, number>): boolean {
        return orderItemData.extras.every((extraItemData: ExtraItemData) => {
            const extraGroup: MenuExtraGroupData = orderItem.availableExtraGroups().find((extraData: MenuExtraGroupData) => extraData.name === extraItemData.groupNameSignal());
            let changed: boolean = false;
            if (extraGroup) {
                const extraProductData: MenuExtraProductData = extraGroup.extraProducts.find((extraProductData: MenuExtraProductData) => extraProductData.productLine.sellableId === extraItemData.sellableId);
                if (extraProductData) {
                    if (checkQtyAvailable &&
                        extraProductData.productLine && this.menuModel.sellableQtyAvailable()(extraProductData.productLine.sellableId) != undefined &&
                        (parentQty ?? 1) * extraItemData.quantitySignal() > this.sellableQtyStillAvailable(extraProductData.productLine.sellableId, runningTotal)) {
                        return false;
                    }
                    orderItem.addOrderExtraItem(orderItem.createOrderExtraItem(extraGroup, extraProductData, extraItemData.quantitySignal()));
                    changed = true;
                }
            }
            if (changed) {
                this.notifyOrderChange();
            }
            return true;
        });
    }

    private createOrderItemFromOrderItemData(orderItemData: OrderItemData,
                                             menuItem: MenuItemData,
                                             sellable: MenuSellableData,
                                             checkQtyAvailable: boolean,
                                             parentQty: number,
                                             runningTotal: Map<number, number>): OrderItem {
        if (!sellable) {
            sellable = menuItem.sellable;
        }
        const minQty: number = sellable.minWebOrderQty || 1;
        let qtyToOrder: number;
        if (checkQtyAvailable && sellable && this.menuModel.sellableQtyAvailable()(sellable.sellableId) != undefined) {
            const qtyAvailable: number = this.sellableQtyStillAvailable(sellable.sellableId, runningTotal);
            if (parentQty != undefined && parentQty * orderItemData.quantitySignal() > qtyAvailable) {
                return undefined;
            }
            qtyToOrder = Math.min(Math.max(orderItemData.quantitySignal(), minQty), qtyAvailable);
        } else {
            qtyToOrder = Math.max(orderItemData.quantitySignal(), minQty);
        }
        if (qtyToOrder < minQty) {
            return undefined;
        }
        const orderItem: OrderItem = new OrderItem(undefined, menuItem, sellable, orderItemData.nameSignal(), qtyToOrder, orderItemData.dealIdSignal(), orderItemData.transferRequestId, orderItemData.transferRequestName, orderItemData.itemNotesSignal(), orderItemData.proposed);
        if (!this.addExtrasFromOrderItemData(orderItem, orderItemData, checkQtyAvailable, (parentQty ?? 1) * qtyToOrder, runningTotal)) {
            return undefined;
        }
        if (checkQtyAvailable && parentQty == undefined) {
            orderItem.qtyOrderedPerSellableId().forEach((qty: number, sellableId: number) =>
                runningTotal.set(sellableId, (runningTotal.get(sellableId) ?? 0) + qty));
        }
        return orderItem;
    }

    private createOrderPlgItemFromOrderItemData(orderItemData: OrderItemData,
                                                sellable: MenuSellableData,
                                                plg: MenuPlgData,
                                                checkQtyAvailable: boolean,
                                                parentQty: number,
                                                runningTotal: Map<number, number>): OrderItem {
        if (checkQtyAvailable && sellable && this.menuModel.sellableQtyAvailable()(sellable.sellableId) != undefined && (parentQty || 1) * orderItemData.quantitySignal() > this.sellableQtyStillAvailable(sellable.sellableId, runningTotal)) {
            return undefined;
        }
        const orderItem: OrderItem = new OrderPlgItem(undefined, plg, orderItemData.nameSignal(), sellable, orderItemData.quantitySignal(), undefined, orderItemData.transferRequestId, orderItemData.transferRequestName);
        if (!this.addExtrasFromOrderItemData(orderItem, orderItemData, checkQtyAvailable, (parentQty || 1) * orderItemData.quantitySignal(), runningTotal)) {
            return undefined;
        }
        return orderItem;
    }

    private createOrderCompositeItemFromOrderItemData(orderItemData: OrderItemData,
                                                      menuItem: MenuItemData,
                                                      checkQtyAvailable: boolean = true,
                                                      runningTotal: Map<number, number>): OrderCompositeItem {
        const qtyToOrder: number = checkQtyAvailable && this.menuModel.sellableQtyAvailable()(orderItemData.sellableId) != undefined
            ? Math.min(orderItemData.quantitySignal(), this.sellableQtyStillAvailable(orderItemData.sellableId, runningTotal))
            : orderItemData.quantitySignal();
        if (qtyToOrder <= 0) {
            return undefined;
        }
        const composite: MenuCompositeData = menuItem.sellable.composite;
        const compositeLines: MenuQtyData[] = this.menuModel.getCompositeLines(composite);
        const components: OrderItem[] = [];
        if (!orderItemData.compositeComponentsSignal().every((compositeOrderItemData: OrderItemData, i: number) => {

            // We can assume positional correspondence between the composite lines in the OrderItemData and the definition.
            const compositeLine: MenuQtyData = compositeLines[i];

            // If the sellable being added doesn't match the definition, or if adding it would exceed the available quantity, then the entire composite is abandoned.
            if (this.menuModel.isProductLinePlgQty(compositeLine)) {
                const plg: MenuPlgData = (<MenuProductLinePlgQtyData>compositeLine).plg;
                const plgSellable: MenuPlgSellableData = plg.sellableLines.find((sellableLine: MenuPlgSellableData) => sellableLine.sellable.sellableId === compositeOrderItemData.sellableId);
                if (!plgSellable) {
                    return false;
                }
                const item: OrderItem = this.createOrderPlgItemFromOrderItemData(compositeOrderItemData, plgSellable.sellable, plg, checkQtyAvailable, orderItemData.quantitySignal(), runningTotal);
                if (!item) {
                    return false;
                }
                components.push(item);
            } else if (this.menuModel.isProductLineQty(compositeLine)) {
                const sellable: MenuSellableData = (<MenuProductLineQtyData>compositeLine).productLine;
                if (sellable.sellableId !== compositeOrderItemData.sellableId) {
                    return false;
                }
                const item: OrderItem = this.createOrderItemFromOrderItemData(compositeOrderItemData, sellable.menuItem, sellable, checkQtyAvailable, orderItemData.quantitySignal(), runningTotal);
                if (!item) {
                    return false;
                }
                components.push(item);
            }
            return true;
        })) {
            return undefined;
        }
        const orderCompositeItem: OrderCompositeItem = new OrderCompositeItem(composite, orderItemData.nameSignal(), menuItem, menuItem.sellable, qtyToOrder, components, orderItemData.transferRequestId, orderItemData.transferRequestName, orderItemData.itemNotesSignal(), orderItemData.dealIdSignal(), orderItemData.proposed);
        if (checkQtyAvailable) {
            orderCompositeItem.qtyOrderedPerSellableId().forEach((qty: number, sellableId: number) =>
                runningTotal.set(sellableId, (runningTotal.get(sellableId) ?? 0) + qty));
        }
        return orderCompositeItem;
    }

    public async pollTransferRequests(transferTargetId: string, tableNumber: string, beaconId: string): Promise<OrderTransferTargetStatus> {
        try {
            const orderTransferRequests: OrderTransferRequestsData = <OrderTransferRequestsData>EntityData.create(OrderTransferRequestsData)
                .assign(await this.orderTransferRequestPollService.performAction({
                    transferTargetId: transferTargetId,
                    orderType: OrderType.toString(this.orderingContextModel.orderType()),
                    tableNumber: tableNumber,
                    beaconId: beaconId
                }));

            // Add items from transfer requests into the order
            const items: OrderItemData[] = [];
            orderTransferRequests.requests.forEach((request: OrderInitiationData) => {
                request.items.forEach((orderItemData: OrderItemData) => {
                    if (!orderItemData.transferRequestId) {
                        orderItemData.transferRequestId = request.requestId;
                        orderItemData.transferRequestName = request.firstName;
                    }
                    if (!this._orderItems().some((existing: OrderItem) => existing.transferRequestId === orderItemData.transferRequestId)) {
                        items.push(orderItemData);
                    }
                });
            });
            if (items.length) {
                this.addOrderItems(this.recreateOrderItems(items, false));
            }
            this.recentOrderService.keepCurrentOrderAlive();

            // Server cannot distinguish whether the group order has been closed due to timeout or due to order completion.
            if (orderTransferRequests.transferTargetStatus === OrderTransferTargetStatus.CLOSED_TIMEOUT && this.hasOrderPlacement()) {
                orderTransferRequests.transferTargetStatus = OrderTransferTargetStatus.CLOSED;
            }

            return orderTransferRequests.transferTargetStatus;
        } catch (error) {
            console.log("ignoring failed poll for transfer target " + transferTargetId);
            return OrderTransferTargetStatus.OPEN;
        }
    }

    public async completeOrder(retainTable?: boolean): Promise<void> {
        this.recentOrderService.deleteCurrentOrder();
        this._canReorderOnLoad = false;
        return this.sessionModel.restartAfterOrderCompletion(retainTable);
    }


    /**
     * Retrieves the available order times for a date.
     * @param expectedCompletion Date that the order is required as a local-time string in the timezone of the store, in the format of yyyy-MM-dd
     */
    public async getAvailableOrderTimes(expectedCompletion: string): Promise<AvailableOrderTimesResponseData> {
        const request: AvailableOrderTimesRequestData = <AvailableOrderTimesRequestData>EntityData.create(AvailableOrderTimesRequestData);
        request.expectedCompletion = expectedCompletion;
        request.orderTypeEnum = this.orderingContextModel.orderType();
        request.allowCarryOverTimes = true;
        request.items = this._orderItems()
            .filter((orderItem: OrderItem) => orderItem.quantitySignal() > 0)
            .map((orderItem: OrderItem) => this.buildOrderItemData(orderItem));
        return <AvailableOrderTimesResponseData>EntityData.create(AvailableOrderTimesResponseData).assign(await this.availableOrderTimesService.performAction(request));
    }

    /**
     * Returns false until a menu has been loaded and a store has been selected.
     * Returns true the first time those conditions are met.
     * Returns false subsequently.
     */
    public get canRestoreOnLoad(): boolean {
        return this.menuModel.currentMenu() && this.orderingContextModel.currentStore() &&
            this._canRestoreOnLoad && !(this._canRestoreOnLoad = false);
    }

    /**
     * Returns false until initial load has been completed, a menu has been loaded and a store and order type have been selected.
     * Returns true the first time those conditions are met.
     * Returns false subsequently.
     */
    public get canReorderOnLoad(): boolean {
        return this.sessionModel.initialLoadStatus() === LoadingStatus.LOADED &&
            this.menuModel.currentMenu() &&
            this.orderingContextModel.currentStore() &&
            this.orderingContextModel.orderType() &&
            this._canReorderOnLoad && !(this._canReorderOnLoad = false);
    }

    public set canReorderOnLoad(canReorderOnLoad: boolean) {
        this._canReorderOnLoad = canReorderOnLoad;
    }

    public get canPromptForReorder(): boolean {
        return this.configurationModel.isConsumerActor() &&
            this.menuModel.currentMenu() &&
            !this.menuModel.itemOrderPending() &&
            this.orderingContextModel.orderType() &&
            this.orderingContextModel.currentStore() &&
            this.isOrderValid() &&
            this._orderInitiationStatus() === OrderInitiationStatus.INITIATED &&
            !this.orderHasItems() &&
            this.getReorderableOrderItems().length > 0;
    }

    public formatPlacedDateTime(placedOrder: PlacedOrderData): string {
        if (!placedOrder) {
            return "";
        }
        return formatDateForDisplay(placedOrder.placedDateTime) + " • " + formatTimeForDisplay(placedOrder.placedDateTime);
    }

    public formatEstimatedCompletionDateTime(placedOrder: PlacedOrderData): string {
        if (!placedOrder) {
            return "";
        }
        const date: Date = EntityData.parseLocalDateTime(placedOrder.estimatedCompletionLocalDateTime);
        return formatDateForDisplay(date) + " • " + formatTimeForDisplay(date);
    }

    public formatRequiredDateTime(initiatedOrder: InitiatedOrderData): string {
        if (!initiatedOrder) {
            return "";
        }
        const date: Date = EntityData.parseLocalDateTime(initiatedOrder.orderLaterLocalDateTime);
        return formatDateForDisplay(date) + " • " + formatTimeForDisplay(date);
    }

    public async editOrderItem(orderItem: DisplayableOrderItem, fromNotification: boolean = false): Promise<void> {
        void this.analyticsService.trackEditItem(orderItem, fromNotification);
        const editedOrderItem: OrderItem = await this.navigationService.createPage("woa-order-item-edit-page", {orderItem: orderItem});
        if (editedOrderItem && editedOrderItem.quantitySignal()) {
            void this.analyticsService.trackItemEdited(editedOrderItem);
        }
    }

    public removeDealItems(dealIds: string[]): void {
        if (!this.isOrderValid()) {
            return;
        }

        this._orderItems()
            .filter((orderItem) => dealIds.indexOf(orderItem.dealIdSignal()) >= 0)
            .forEach((orderItem) => this.removeOrderItemByIdentifier(orderItem.identifier));

        // Save the current order without the deals.
        if (this.orderInitiation()) {
            const orderInitiation: OrderInitiationData = this.orderInitiation();
            orderInitiation.deals = undefined;
            this.recentOrderService.saveCurrentOrder(orderInitiation, this._orderNotes(), undefined);
        }
    }

    public validateOrderItem(orderItem: OrderItem): boolean {
        if (orderItem.validationMessage()) {
            this.addToOrderAttempted.set(true);
            void this.notificationService.displayFooterMessage(orderItem.validationMessage());
            return false;
        }
        return true;
    }

    public getReorderableOrderItems(): OrderItem[] {
        const recentOrders: RecentOrderData[] = this.recentOrderService.recentOrders;
        if (recentOrders.length == 0) {
            return [];
        }

        // only consider orders that are no older than 8 hours
        const cutOffDate: Date = new Date();
        cutOffDate.setHours(cutOffDate.getHours() - OrderModel.REORDER_ROUND_CUTOFF_HOURS);
        if (recentOrders[0].initiatedOrder.initiatedDateTime.getTime() < cutOffDate.getTime()) {
            return [];
        }

        return this.recreateOrderItems(recentOrders[0].orderInitiation.items
            .filter((orderItemData: OrderItemData) =>
                orderItemData.reorderableInApp &&
                (!orderItemData.transferRequestId || this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE)));
    }

    private async removeOversoldItems(): Promise<void> {
        const dealIds: Set<string> = new Set<string>();
        let modified: boolean = false;
        [...this._orderItems()].forEach((orderItem: OrderItem) => {
            while (orderItem.isAtLimit()(0)) {
                modified = true;
                if (orderItem.quantity > (orderItem.sellable().minWebOrderQty || 1)) {
                    orderItem.quantity--;
                } else {
                    this.removeOrderItemByIdentifier(orderItem.identifier);
                    break;
                }
                if (orderItem.dealIdSignal()) {
                    dealIds.add(orderItem.dealIdSignal());
                }
            }
        });
        if (dealIds.size > 0) {
            this.dealModel.removeDeals((deal: DealData) => dealIds.has(deal.id));
        }
        if (modified && this.sessionModel.orderingPhase() === OrderingPhase.ORDERING && !this.removalOfOversoldItemsReported()) {
            await this.notificationService.displayAlert(null, "woa.message.order.itemsNotAvailableNowUnplaced");
        }
        this.removalOfOversoldItemsReported.set(false);
    }

    /**
     * Removes order items that are unavailable, according to the specified request.
     * @returns true is there is nothing to remove, or if the unavailable items are removed.
     * @returns false if confirm is specified as true and the user rejects the removal.
     * @param request
     */
    public async removeUnavailableOrderItems(request: UnavailableOrderItemRemovalRequest): Promise<boolean> {
        if (!this.isOrderValid()) {
            return true;
        }

        const unavailableItems: OrderItem[] = this._orderItems().filter((orderItem: OrderItem) =>
            ((request.existence && !this.menuModel.sellableIsAvailable()(orderItem.sellable().sellableId)) ||
                (request.schedules && !this.menuModel.itemIsAvailableOnSchedules()(orderItem.menuItem())) ||
                (request.orderType && !this.menuModel.itemIsAvailableForOrderType()(orderItem.menuItem(), request.orderType))));
        if (!unavailableItems.length) {
            return true;
        }

        if (request.confirm) {
            const confirmationMessage: string = request.schedules ? "woa.message.order.itemsUnavailableAtTime"
                : request.orderType === OrderType.ORDER_AHEAD ? "woa.message.order.itemsUnavailableOrderAhead"
                    : request.orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.itemsUnavailableOrderDelivery"
                        : "woa.message.order.itemsUnavailableOrderAtTable";

            let itemNames: string = unavailableItems.map((item) => this.menuModel.getMenuItemDisplayName(item.menuItem())).join(", ");
            if (itemNames.length > 70) {
                itemNames = itemNames.substring(0, 70) + "...";
            }
            this.loadingService.dismissSpinner();
            if (!await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveUnavailableItems", undefined,
                confirmationMessage, "woa.button.common.cancel", "woa.button.common.proceed", true, {items: itemNames})) {
                return false;
            }
        }

        const dealIds: Set<string> = new Set<string>();
        unavailableItems.forEach((orderItem: OrderItem) => {
            console.log("Removing item from order: " + orderItem.sellable().name);
            this.removeOrderItemByIdentifier(orderItem.identifier);
            if (orderItem.dealIdSignal()) {
                dealIds.add(orderItem.dealIdSignal());
            }
        });
        if (dealIds.size > 0) {
            this.dealModel.removeDeals((deal: DealData) => dealIds.has(deal.id));
        }
        return true;
    }

    private buildOrderItemData(orderItem: OrderItem): OrderItemData {
        const orderItemData: OrderItemData = <OrderItemData>EntityData.create(OrderItemData);
        orderItemData.identifier = orderItem.identifier;
        orderItemData.sellableId = orderItem.sellable().sellableId;
        orderItemData.name = orderItem.nameSignal();
        orderItemData.quantity = orderItem.quantitySignal();
        orderItemData.unitPrice = orderItem.unitPriceSignal();
        if (orderItem.menuItem()) {
            orderItemData.courseId = orderItem.menuItem().courseId;
        }
        orderItemData.applyRsa = orderItem.sellable().applyRsa;
        orderItemData.reorderableInApp = orderItem.sellable().reorderableInApp;
        orderItemData.transferRequestId = orderItem.transferRequestId;
        orderItemData.transferRequestName = orderItem.transferRequestName;
        if (orderItem.itemNotesSignal()) {
            const tag: OrderMetaTagData = <OrderMetaTagData>EntityData.create(OrderMetaTagData);
            tag.name = OrderMataTagName.ORDER_MSG;
            tag.type = OrderMataTagType.MESSAGE;
            tag.value = orderItem.itemNotesSignal();
            orderItemData.tags = [tag];
        }
        orderItemData.dealId = orderItem.dealIdSignal();
        orderItemData.proposed = orderItem.proposed();
        orderItemData.extras = orderItem.orderedExtrasSignal().map((orderExtraItem: OrderExtraItem) => {
            const extraItemData: ExtraItemData = <ExtraItemData>EntityData.create(ExtraItemData);
            extraItemData.sellableId = orderExtraItem.extraProduct.productLine.sellableId;
            extraItemData.name = orderExtraItem.nameSignal();
            extraItemData.quantity = orderExtraItem.quantitySignal();
            extraItemData.unitPrice = orderExtraItem.unitPriceSignal();
            extraItemData.isGroup = orderExtraItem.isGroupSignal();
            extraItemData.isVisible = orderExtraItem.isVisibleSignal();
            extraItemData.isNested = orderExtraItem.isNestedSignal();
            extraItemData.groupName = orderExtraItem.groupNameSignal();
            return extraItemData;
        });
        if (orderItem instanceof OrderCompositeItem) {
            orderItemData.compositeOrderItems = orderItem.compositeComponentsSignal().map((component: OrderItem) => this.buildOrderItemData(component));
        }
        return orderItemData;
    }

    /**
     * Registers the order with the server for pricing, availability, etc
     * @param reason Logged to console
     * @param initiateIfNewOrder Indicates that the order should be initiated if a new order is created. This is needed if the order has products or other info at the time of creating the order.
     */
    public requestOrderInitiation(reason: string, initiateIfNewOrder: boolean = false): void {
        console.log(Date.now(), "order initiation requested:", reason);
        if (this.orderSubmissionStatus() === OrderSubmissionStatus.SUBMITTING) {
            console.log("suppressing order initiation - order placement has commenced");
            return;
        }

        this._orderErrorStates.set(new OrderErrorStates());

        if (this.orderingContextModel.orderType() === OrderType.ORDER_AT_TABLE && this.orderingContextModel.expectedCompletion()) {
            if (this.orderingContextModel.currentStore() && (this.orderingContextModel.currentStore().offline || this.orderingContextModel.currentStore().closed)) {
                this._orderErrorStates().orderWithInvalidTimeAttempted.set(true);
            } else {
                this._orderErrorStates().tableOrderWithTimeAttempted.set(true);
            }
        }

        if (!this.isOrderValid()) {
            this.startNewOrder("order initiation");
            if (!initiateIfNewOrder) {
                return;
            }
        }
        if (!this.canInitiateOrder()) {
            console.log("insufficient information to initiate order");
            return;
        }

        // Once an initiation has been requested, we treat the order as being in INITIATING status.
        // This is important for untilOrderInitiationComplete() to work properly.
        this._orderInitiationStatus.set(OrderInitiationStatus.INITIATING);

        this.orderInitiationRequested$.next();
    }

    private async initiateOrder(): Promise<void> {
        if (this.orderSubmissionStatus() === OrderSubmissionStatus.SUBMITTING) {
            console.log("suppressing order initiation - order placement has commenced");
            return;
        }

        console.log("initiating order");
        this._orderInitiationStatus.set(OrderInitiationStatus.INITIATING);

        // The "checkIntegratedDeliveryTime" flag on order initiation is used to limit the frequency of integrated delivery address lookups.
        // Quick pay is not enabled for deliveries since an opportunity must be required to enter or amend delivery instructions.
        // Therefore, delivery "checkout" always occurs via the payment page.
        const checkIntegratedDeliveryTime: boolean = this.sessionModel.orderingPhase() === OrderingPhase.PAYMENT;

        // The "forPayment" flag on order initiation is used to control whether payment details are expected to be returned with the initiated order (if required for the payment platform).
        // For now, we require this only when on the payment page - this may change depending on Tyro's ability to handle quick pay flows.
        // Stripe doesn't require this, but the flag is ignored in that case.
        const forPayment: boolean = this.sessionModel.orderingPhase() === OrderingPhase.PAYMENT;
        const paymentCustomerDetails: PaymentCustomerDetails = forPayment ? this.customerDetailsService.loadPaymentCustomer() : undefined;

        try {
            let interimInitiationStatus: OrderInitiationStatus = OrderInitiationStatus.INITIATING;

            // Retry loop
            let attempts: number = 0;
            while (true) {
                attempts++;

                // Build the order initiation data
                const orderInitiation: OrderInitiationData = <OrderInitiationData>EntityData.create(OrderInitiationData);
                orderInitiation.requestId = uuid();
                orderInitiation.orderTypeEnum = this.orderingContextModel.orderType();
                orderInitiation.tableLookupCode = this.orderingContextModel.currentStore().table.tableLookupCode;
                orderInitiation.tableNumber = this.orderingContextModel.currentStore().table.tableNumber;
                orderInitiation.beaconId = this.sessionModel.beaconId();
                if (this.groupOrderModel.currentGroupOrder()) {
                    orderInitiation.orderTransferTargetId = this.groupOrderModel.currentGroupOrder().id;
                    orderInitiation.transferTargetName = this.groupOrderModel.currentGroupOrder().name;
                }
                orderInitiation.platformCustomerId = paymentCustomerDetails ? paymentCustomerDetails.paymentCustomerId : undefined;
                orderInitiation.forPayment = forPayment;
                orderInitiation.checkIntegratedDeliveryTime = checkIntegratedDeliveryTime;

                // Address is required for delivery order initiation.
                if (checkIntegratedDeliveryTime) {
                    orderInitiation.address = this.orderingContextModel.address() && this.orderingContextModel.address().street ? this.orderingContextModel.address() : this.identityModel.currentIdentity().homeAddress;
                }

                orderInitiation.firstName = this.orderingContextModel.trimmedFirstName() || "NOT ASSIGNED";
                orderInitiation.lastName = this.orderingContextModel.trimmedLastName() || "NOT ASSIGNED";
                if (this.orderingContextModel.orderType() !== OrderType.ORDER_AT_TABLE) {
                    orderInitiation.expectedCompletion = this.orderingContextModel.expectedCompletionAsString();
                }
                orderInitiation.deals = this.dealModel.appliedDeals().map((deal: DealData) => OrderDealData.fromDeal(deal));
                orderInitiation.items = this._orderItems()
                    .filter((orderItem: OrderItem) => orderItem.quantitySignal() > 0)
                    .map((orderItem: OrderItem) => this.buildOrderItemData(orderItem));
                orderInitiation.tipAmount = this.tipAmount();
                this.orderInitiation.set(orderInitiation);

                // Persist the current order
                const keepAsCurrentOrder: boolean = this.canPersistOrderDetails() &&
                    (this.hasOrderBeenPersisted() || this.personalOrderStarted() || this.groupOrderModel.groupOrderStatus() === OrderTransferTargetStatus.OPEN);
                if (keepAsCurrentOrder) {
                    this.hasOrderBeenPersisted.set(true);
                }
                if (keepAsCurrentOrder) {
                    this.recentOrderService.saveCurrentOrder(this.orderInitiation(), this._orderNotes(), this.dealModel.appliedDeals());
                }

                // Perform the initiation
                const expectedInitiationSequence: number = ++this.initiationSequence;
                interimInitiationStatus = OrderInitiationStatus.INITIATING;
                console.log("requesting order initiation with sequence nr " + expectedInitiationSequence);
                try {
                    const result: any = await this.orderInitiationService.performAction(this.orderInitiation());
                    if (expectedInitiationSequence !== this.initiationSequence) {
                        console.log("ignoring response to superseded initiation request with sequence nr " + expectedInitiationSequence);
                        return;
                    }
                    if (this._orderInitiationStatus() === OrderInitiationStatus.UNINITIATED) {
                        return;
                    }

                    console.log("assigning response for order initiation with sequence nr " + expectedInitiationSequence);
                    this._initiatedOrder.set(<InitiatedOrderData>EntityData.create(InitiatedOrderData).assign(result));
                    this.orderingContextModel.currentStore().completionTime = this._initiatedOrder().store.completionTime;
                    this.orderingContextModel.currentStore().currentWaitTimeMinutes = this._initiatedOrder().store.currentWaitTimeMinutes;
                    this._initiatedOrder().store = this.orderingContextModel.currentStore();
                    this._initiatedOrder().tipAmount = this.tipAmount();
                    this._initiatedOrder().initiatedOrderItems.forEach((initiatedOrderItem: InitiatedOrderItemData) => {
                        const orderItem: OrderItem = this._orderItems().find((orderItem: OrderItem) => orderItem.identifier === initiatedOrderItem.identifier);
                        if (orderItem) {
                            orderItem.justOrdered.set(orderItem.initiatedTotalSignal() == undefined);
                            orderItem.initiatedTotalSignal.set(initiatedOrderItem.total);
                            orderItem.initiatedExtrasTotalSignal.set(initiatedOrderItem.extrasTotal);
                            if (orderItem instanceof OrderCompositeItem) {
                                orderItem.compositeComponentsSignal().forEach((compositeOrderItem: OrderItem) =>
                                    compositeOrderItem.initiatedExtrasTotalSignal.set(initiatedOrderItem.compositeOrderItems.find((initiatedCompositeOrderItem: InitiatedOrderItemData) =>
                                        initiatedCompositeOrderItem.identifier === compositeOrderItem.identifier).extrasTotal));
                            }
                        }

                        const initiationOrderItem: OrderItemData = this.orderInitiation().items.find((initiationOrderItem: OrderItemData) => initiationOrderItem.identifier === initiatedOrderItem.identifier);
                        if (initiationOrderItem) {
                            initiationOrderItem.initiatedTotal = initiatedOrderItem.total;
                            initiationOrderItem.initiatedExtrasTotal = initiatedOrderItem.extrasTotal;
                            initiationOrderItem.compositeComponentsSignal().forEach((compositeOrderItem: OrderItemData) =>
                                compositeOrderItem.initiatedExtrasTotal = initiatedOrderItem.compositeOrderItems.find((initiatedCompositeOrderItem: InitiatedOrderItemData) =>
                                    initiatedCompositeOrderItem.identifier === compositeOrderItem.identifier).extrasTotal);
                        }
                    });
                    this.dealModel.appliedDeals().forEach((deal: DealData) => deal.used = this._initiatedOrder().usedDeals.indexOf(deal.id) >= 0);

                    interimInitiationStatus = OrderInitiationStatus.INITIATED;

                    // Initiation successful - terminate retry loop.
                    break;
                } catch (error) {
                    if (expectedInitiationSequence !== this.initiationSequence) {
                        console.log("ignoring response to superseded initiation request with sequence nr " + expectedInitiationSequence);
                        return;
                    }
                    // @ts-ignore
                    if (this._orderInitiationStatus() === OrderInitiationStatus.UNINITIATED) {
                        return;
                    }

                    const displayableMessage: DisplayableDataServerMessage = error instanceof DataClientError ? await error.displayableMessage : undefined;
                    this._initiationErrorKey.set(displayableMessage ? displayableMessage.messageKey : "woa.error.order.initiationFailed");
                    this._initiationErrorParams.set(displayableMessage ? displayableMessage.messageParams : {});
                    interimInitiationStatus = OrderInitiationStatus.FAILED;

                    // Log initiation failure to console
                    let message: string = "OrderModel.initiateOrder - order initiation failed";
                    console.log(message, error);

                    // Retry idiopathic response error
                    if (error instanceof DataClientError && error.response.status === 0) {
                        if (attempts < OrderModel.ORDER_INITIATION_ATTEMPTS_LIMIT) {
                            console.log("retrying initiation after " + attempts + " failed attempt(s)");
                            continue;
                        }
                        console.log("not retrying initiation after " + attempts + " failed attempt(s)");
                        message += " " + attempts + " time(s)";
                    }

                    // Log initiation failure to server
                    this.notificationService.logErrorToServer(message, this.orderInitiation, error);
                    break;
                }
            }

            // Get payment provider and/or key from order initiation if present
            this.paymentModel.applyPaymentDetails(this._initiatedOrder().paymentDetails);

            this._orderInitiationStatus.set(interimInitiationStatus);
            this._orderInitiatedTime.set(new Date().getTime());
            console.log("order initiation complete");
        } catch (error) {
            // initiateOrder() does not reject, but rather resolves with the order in error state - this is here for the unexpected

            console.error(error);
            void this.analyticsService.trackAnomaly("initiateOrder failed", error);
            this.notificationService.logErrorToServer("OrderModel.initiateOrder - order initiation failed unexpectedly", this.orderInitiation, error);

            this._orderInitiationStatus.set(OrderInitiationStatus.FAILED);
            this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.order.initiationFailedFatal"));
        }
    }

    /**
     * Allows the caller to wait for order initiation to complete.
     */
    public async untilOrderInitiationComplete(): Promise<void> {
        await whenSignalMatches(this._orderInitiationStatus, status => status !== OrderInitiationStatus.INITIATING, {
            asyncResolve: true,
            injector: this.injector
        });
    }

    public async validateOrderForPayment(paymentType: PaymentType, applyErrorStates: boolean): Promise<boolean> {
        const orderErrorStates: OrderErrorStates = new OrderErrorStates();
        orderErrorStates.emptyOrderAttempted.set(!this.orderHasItems());

        let prompted: boolean = false;
        if (applyErrorStates && this.timeSelectionRequired()) {
            prompted = true;
            await this.orderingContextModel.changeOrderingContext(true);
        }
        orderErrorStates.orderWithInvalidTimeAttempted.set(this.timeSelectionRequired() || this._initiatedOrder().cannotAllocateOrderLaterDateTime);

        orderErrorStates.minMaxOrderAmountAttempted.set(
            paymentType !== PaymentType.GROUP_PAY && paymentType !== PaymentType.NONE &&
            (this.showMinOrderAheadAmountWarning() || this.showMaxOrderAheadAmountWarning() ||
                this.showMinDeliveryAmountWarning() || this.showMaxDeliveryAmountWarning() ||
                this.showMinOrderAtTableAmountWarning() || this.showMaxOrderAtTableAmountWarning()));
        if (applyErrorStates) {
            this._orderErrorStates.set(orderErrorStates);
        }
        return !prompted && !orderErrorStates.hasErrors();
    }

    /**
     * Build a recent order data object from the order
     */
    buildRecentOrderData(): RecentOrderData {
        const recentOrder: RecentOrderData = <RecentOrderData>EntityData.create(RecentOrderData);
        recentOrder.orderInitiation = this.orderInitiation();
        recentOrder.initiatedOrder = this._initiatedOrder();
        recentOrder.orderPlacement = this.orderPlacement();
        recentOrder.placedOrder = this._placedOrder();
        return recentOrder;
    }

    /**
     * Performs all order placement processing, including validation, getting payment details, error handling, and retry for both errors and 2FA.
     *
     * @param paymentType
     * @param transferTarget Only for PaymentType.GROUP_PAY
     * @param paymentCard Only for PaymentType.NEW_CREDIT_CARD
     * @param validate Perform page-specific validations
     * @param cardValidationFailed Handle card validation errors
     */
    public async placeOrder(paymentType: PaymentType,
                            transferTarget?: OrderTransferTargetReferenceData,
                            paymentCard?: PaymentCard,
                            validate?: () => boolean,
                            cardValidationFailed?: () => void): Promise<boolean> {
        if (paymentType == undefined) {
            return false;
        }

        if (paymentType === PaymentType.TAP_TO_PAY && !this.tapToPayService.isAvailable()) {
            void this.notificationService.displayAlert(null, "woa.error.payment.tapToPayNotAvailable");
            return false;
        }

        // Prevent double-submission
        if (this.orderSubmissionStatus() === OrderSubmissionStatus.SUBMITTING) {
            console.log("Already started placing order");
            if (Date.now() - this.orderPlacementStarted() > OrderModel.ORDER_PLACEMENT_DELAY_TRIGGER_SECONDS * 1000) {
                void this.analyticsService.trackDelay("order placement preparation");
            }
            return false;
        }
        if (this.orderSubmissionStatus() === OrderSubmissionStatus.COMPLETE) {
            console.log("Order already placed");
            void this.analyticsService.trackAnomaly("placement attempt after order placed");
            return false;
        }

        // Sanity check initiation
        if (this.orderInitiation().requestId !== this._initiatedOrder().requestId) {
            console.log("Order initiation outstanding");
            void this.analyticsService.trackAnomaly("placement attempt with initiation outstanding");
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Sanity check payment type
        if ((paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY) && !this.paymentModel.canMakeWalletPayment()) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }
        if (paymentType === PaymentType.SAVED_CREDIT_CARD && !this.paymentModel.hasSavedCreditCard()) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Sanity check table number
        if (this.orderInitiation().orderTypeEnum === OrderType.ORDER_AT_TABLE && !this.orderInitiation().tableNumber) {
            console.log("No table number for table order");
            void this.analyticsService.trackAnomaly("placement attempt without table number");
            void this.notificationService.displayAlert(undefined, this.sessionModel.noTableMessage());
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Order time validation
        if (this._initiatedOrder() && this._initiatedOrder().orderLaterDateTime != undefined && this._initiatedOrder().cannotAllocateOrderLaterDateTime) {
            void this.notificationService.displayAlert(undefined, "woa.error.order.pleaseSelectAnotherTime");
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Perform page-specific validations (if any)
        if (validate && !validate()) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            return false;
        }

        // Warn if placing order for an empty group order
        if (this.groupOrderModel.currentGroupOrder() && !this.totalTransfers() &&
            !await this.notificationService.displayConfirmation("woa.heading.payment.confirmEmptyGroupOrder", undefined,
                "woa.message.payment.confirmEmptyGroupOrder",
                "woa.button.payment.abortEmptyGroupOrder",
                "woa.button.payment.continueEmptyGroupOrder",
                true, undefined)) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            void this.analyticsService.trackPlaceOrderAbandoned("emptyGroupOrderPrompt");
            return false;
        }

        // Prompt for additional information as required
        if (!(this.paymentModel.paymentProvider() === IntegratedPaymentProvider.TYRO && this.sessionModel.orderingPhase() === OrderingPhase.PAYMENT) &&
            this.paymentModel.canTip(paymentType) && !await this.orderingContextModel.promptForTip()) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            void this.analyticsService.trackPlaceOrderAbandoned("tipPrompt");
            return false;
        }
        if (paymentType === PaymentType.GROUP_PAY &&
            !await this.orderingContextModel.promptForFirstName(transferTarget ? new PossessivePipe().transform(transferTarget.name) : undefined)) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            void this.analyticsService.trackPlaceOrderAbandoned("namePrompt");
            return false;
        }
        if ((paymentType === PaymentType.SAVED_CREDIT_CARD || paymentType === PaymentType.NEW_CREDIT_CARD || paymentType === PaymentType.POINTS || paymentType === PaymentType.NONE) &&
            !await this.orderingContextModel.confirmPhoneNumberForSms()) {
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            void this.analyticsService.trackPlaceOrderAbandoned("mobilePhonePrompt");
            return false;
        }

        this.orderSubmissionStatus.set(OrderSubmissionStatus.SUBMITTING);
        this.orderPlacementStarted.set(Date.now());

        const orderPlacedEvent: OrderPlacedEvent = new OrderPlacedEvent(this.orderingContextModel.orderType(), this.identityModel.isGuestUser(),
            this.orderTotalPayment(), paymentType, this.totalItems(),
            this.paymentModel.canMakeWalletPayment(), this.sessionModel.orderingPhase() !== OrderingPhase.PAYMENT,
            this.orderingContextModel.currentStore().table.tableNumber);
        void this.analyticsService.trackPlaceOrderStarted(orderPlacedEvent);

        if (paymentType === PaymentType.GROUP_PAY) {
            if (!transferTarget) {
                transferTarget = this._initiatedOrder().availableTransferTargets.length > 1 ? this._initiatedOrder().availableTransferTargets[0] : undefined;
                if (!transferTarget) {
                    void this.analyticsService.trackAnomaly("group pay attempt with transfer target");
                    this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                    return false;
                }
            }
        } else {
            transferTarget = undefined;
        }

        try {
            let paymentAuthorisation: PaymentAuthorisationData = undefined;

            // Retry loop for 3DS and prompted retry
            let retryRequired: boolean = true;
            let placementAttempt: number = 0;
            let placementStarted: number = undefined;
            while (retryRequired) {
                retryRequired = false;
                placementAttempt++;

                if (!await this.loadingService.performWithSpinner(async () => {

                    // Obtain payment details
                    paymentAuthorisation = await this.paymentModel.authorisePayment(paymentType, paymentCard, paymentAuthorisation, cardValidationFailed);
                    if (!paymentAuthorisation) {
                        return false;
                    }

                    // For a wallet payment, phone number confirmation happens after we get the payment response, since it is likely to be the source of the phone number
                    if (paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY) {
                        if (!await this.orderingContextModel.confirmPhoneNumberForSms(
                            paymentAuthorisation.payerPhone,
                            this.orderingContextModel.showPhoneNumber() || (this.orderingContextModel.marketingOptIn() && this.configurationModel.app().collectMarketingMobilePhone),
                            async () => this.loadingService.dismissSpinner())) {
                            void this.analyticsService.trackPlaceOrderAbandoned("integrated payment mobilePhonePrompt");
                            if (paymentType === PaymentType.APPLE_PAY) {
                                void this.notificationService.displayAlert(null, "woa.error.payment.applePayNotCompleted");
                            } else if (paymentType === PaymentType.GOOGLE_PAY) {
                                void this.notificationService.displayAlert(null, "woa.error.payment.googlePayNotCompleted");
                            } else {
                                void this.notificationService.displayAlert(null, "woa.error.payment.notCompleted");
                            }
                            return false;
                        }
                        await this.loadingService.startSpinner();
                    }

                    const orderCustomerDetails: CustomerDetails = new CustomerDetails();
                    const nameTrusted: boolean = this.configurationModel.isWaiterActor() ||
                        (!!this.orderingContextModel.trimmedFirstName() && this.orderingContextModel.firstNameTrusted()) ||
                        (!!this.orderingContextModel.trimmedLastName() && this.orderingContextModel.lastNameTrusted());
                    const emailTrusted: boolean = this.configurationModel.isWaiterActor() ||
                        (!!this.orderingContextModel.email() && this.orderingContextModel.emailTrusted());
                    const mobilePhoneTrusted: boolean = this.configurationModel.isWaiterActor() ||
                        (!!this.orderingContextModel.mobilePhone() && this.orderingContextModel.mobilePhoneTrusted());
                    let marketingOptIn: boolean = undefined;

                    // Untrusted fields in the ordering context are not used for the order unless they are shown.
                    if (nameTrusted || this.orderingContextModel.showName()) {
                        orderCustomerDetails.firstName = this.orderingContextModel.trimmedFirstName();
                        orderCustomerDetails.lastName = this.orderingContextModel.trimmedLastName();
                    }
                    if (emailTrusted || this.orderingContextModel.showEmail()) {
                        orderCustomerDetails.email = this.orderingContextModel.email();
                        if (this.configurationModel.isConsumerActor()) {
                            marketingOptIn = this.orderingContextModel.marketingOptIn();
                        }
                    }
                    if (mobilePhoneTrusted || this.orderingContextModel.showPhoneNumber()) {
                        orderCustomerDetails.mobilePhone = this.orderingContextModel.mobilePhone();
                        if (this.configurationModel.isConsumerActor()) {
                            marketingOptIn = this.orderingContextModel.marketingOptIn();
                        }
                    }

                    // Update trusted customer contact details in browser storage.
                    if (this.configurationModel.isConsumerActor()) {
                        const savedCustomerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();
                        if (nameTrusted) {
                            savedCustomerDetails.firstName = this.orderingContextModel.trimmedFirstName();
                            savedCustomerDetails.lastName = this.orderingContextModel.trimmedLastName();
                        }
                        if (emailTrusted) {
                            savedCustomerDetails.email = this.orderingContextModel.email();
                        }
                        if (mobilePhoneTrusted) {
                            savedCustomerDetails.mobilePhone = this.orderingContextModel.mobilePhone();
                        }
                        if (marketingOptIn !== undefined) {
                            savedCustomerDetails.marketingOptInDecided = true;
                        }
                        savedCustomerDetails.lastPaymentType = paymentType;
                        this.customerDetailsService.saveCustomer(savedCustomerDetails);
                    }

                    // Place the order
                    try {

                        // Populate order placement details
                        if (paymentType === PaymentType.GROUP_PAY && transferTarget) {
                            this.orderInitiation().firstName = this.orderingContextModel.firstName();
                            this.orderInitiation().orderTransferTargetId = this.groupOrderModel.currentGroupOrder() ? this.groupOrderModel.currentGroupOrder().id : undefined;
                            this.orderInitiation().transferTargetId = transferTarget.id;
                            this.orderInitiation().transferTargetName = transferTarget.name;
                            this.orderInitiation.set(<OrderInitiationData>EntityData.create(OrderInitiationData).assign(this.orderInitiation()));
                            this.orderPlacement.set(undefined);
                        } else {
                            if (!this._initiatedOrder()) {
                                this.orderPlacement.set(undefined);
                                // noinspection ExceptionCaughtLocallyJS
                                throw new OrderError(OrderErrorCode.ORDER_NOT_INITIATED, "Cannot build order placement - order has not been initiated");
                            }
                            if (!this._initiatedOrder().orderToken) {
                                // noinspection ExceptionCaughtLocallyJS
                                throw new OrderError(OrderErrorCode.ORDER_TOKEN_MISSING, "Cannot build order placement - no order token available");
                            }

                            const orderPlacement: OrderPlacementData = <OrderPlacementData>EntityData.create(OrderPlacementData);
                            orderPlacement.requestId = this._initiatedOrder().requestId;
                            orderPlacement.orderToken = this._initiatedOrder().orderToken;
                            orderPlacement.paymentAuthorisation = paymentAuthorisation;
                            orderPlacement.firstName = orderCustomerDetails.firstName;
                            orderPlacement.lastName = orderCustomerDetails.lastName;
                            orderPlacement.email = orderCustomerDetails.email;
                            orderPlacement.mobilePhone = orderCustomerDetails.mobilePhone;
                            orderPlacement.nameTrusted = nameTrusted;
                            orderPlacement.emailTrusted = emailTrusted;
                            orderPlacement.mobilePhoneTrusted = mobilePhoneTrusted;
                            orderPlacement.marketingOptIn = marketingOptIn;
                            orderPlacement.customerNotes = this._orderNotes();
                            orderPlacement.address = this.orderingContextModel.address();
                            orderPlacement.deliveryInstructions = this.orderingContextModel.deliveryInstructions();
                            orderPlacement.saveAddressForMember = this.orderingContextModel.saveAddressForMember() && this.identityModel.currentMember().allowProfileEdit;
                            orderPlacement.appSessionId = this.sessionModel.appSessionId();
                            orderPlacement.scanId = this.sessionModel.scanId();
                            orderPlacement.appUserId = this.appUserService.getAppUserId();
                            orderPlacement.orderTransferTargetId = this.groupOrderModel.currentGroupOrder() ? this.groupOrderModel.currentGroupOrder().id : undefined;
                            orderPlacement.checkAvailableQtys = this.orderingContextModel.isStoreOpenAtCompletionTime();
                            if (this.configurationModel.requireWaiterName()) {
                                orderPlacement.waiterName = this.identityModel.waiterName();
                            }
                            orderPlacement.orderItemSchedules = this._orderItems().map((orderItem: OrderItem) => {
                                const orderItemSchedules: OrderItemSchedulesData = <OrderItemSchedulesData>EntityData.create(OrderItemSchedulesData);
                                orderItemSchedules.itemIdentifier = orderItem.identifier;
                                orderItemSchedules.mergedSchedules = orderItem.menuItem().mergedSchedules;
                                return orderItemSchedules;
                            });
                            this.orderPlacement.set(orderPlacement);
                        }

                        // Persist the recent order details
                        if (this.canPersistOrderDetails()) {
                            const recentPendingOrder: RecentOrderData = this.buildRecentOrderData();
                            recentPendingOrder.pending = true;
                            this.recentOrderService.addRecentOrder(recentPendingOrder);
                        }

                        console.log("placing order");
                        if (!placementStarted) {
                            placementStarted = Date.now();
                        }
                        try {
                            this._placedOrder.set(<PlacedOrderData>EntityData.create(PlacedOrderData).assign((await (paymentType === PaymentType.GROUP_PAY
                                ? this.orderTransferService.performAction(this.orderInitiation())
                                : this.orderPlacementService.performAction(this.orderPlacement())))));

                            // update the app user id in case the server used another id when placing the order
                            if (this._placedOrder().appUserId && this._placedOrder().appUserId !== this.appUserService.getAppUserId()) {
                                this.appUserService.updateAppUserId(this._placedOrder().appUserId);
                            }

                            if (this.canPersistOrderDetails() && this._placedOrder().status === OrderPlacementStatus.ACCEPTED) {
                                this.recentOrderService.addRecentOrder(this.buildRecentOrderData());
                                this.recentOrderService.deleteCurrentOrder();
                            }
                        } catch (error) {
                            this.orderPlacement.set(undefined);
                            this.recentOrderService.deletePendingOrder(this._initiatedOrder().requestId);
                            // noinspection ExceptionCaughtLocallyJS
                            throw error;
                        }

                        // Send the order-placed event here if the placement is complete.
                        // If it is still pending (foreground and integrated order processing), we pass the event on the order completion page to send (or not) depending on the eventual result.
                        if (this._placedOrder().status === OrderPlacementStatus.ACCEPTED) {
                            void this.analyticsService.trackOrderPlaced(orderPlacedEvent);
                        }
                        if (this._orderNotes()) {
                            void this.analyticsService.trackOrderPlacedWithOrderNotes();
                        }
                        if (this._orderItems().some((item: OrderItem) => !!item.itemNotesSignal())) {
                            void this.analyticsService.trackOrderPlacedWithItemNotes();
                        }

                        this.paymentModel.completePayment(paymentAuthorisation);

                        console.log("order placement complete");

                        this.loadingService.dismissSpinner();
                        this.orderPlacementFinished.set(Date.now());
                        this.orderSubmissionStatus.set(OrderSubmissionStatus.COMPLETE);

                        void this.navigationService.createPage("woa-order-confirmation-page", {orderPlacedEvent: orderPlacedEvent}, undefined, this._placedOrder().status !== OrderPlacementStatus.PENDING);
                    } catch (error) {
                        console.error(error);
                        this.loadingService.dismissSpinner();
                        let paymentAuthorisationFailed: boolean = false;

                        // Handle expected errors including 3DS, and retry where possible.
                        const conditionNotification: DataServerConditionNotification = error instanceof DataClientError ? await error.conditionNotification : undefined;
                        const displayableMessage: DisplayableDataServerMessage = error instanceof DataClientError ? await error.displayableMessage : undefined;

                        // Order is not initiated or no order token - cause unknown - possible race condition.
                        if (error instanceof OrderError && (error.code === OrderErrorCode.ORDER_NOT_INITIATED || error.code === OrderErrorCode.ORDER_TOKEN_MISSING)) {
                            void this.analyticsService.trackPlaceOrderFailed("invalidOrderInitiationState");
                            if (Date.now() - placementStarted < OrderModel.ORDER_PLACEMENT_AUTO_RETRY_WINDOW_MS ||
                                await this.notificationService.displayConfirmation("woa.heading.order.invalidInitiationState", undefined, "woa.error.order.invalidInitiationState",
                                    "woa.button.order.return", "woa.button.common.retry", false, undefined, true)) {
                                retryRequired = true;
                            } else {
                                this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                            }
                        }

                            // Duplicate order attempt - If this is the first placement attempt, log and ignore (no reporting to user).
                        // If this happens on a retry, however we treat it like another other error.
                        else if (error.status === 409 && placementAttempt == 1) {
                            void this.analyticsService.trackPlaceOrderFailed("duplicateAttemptIgnored");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                        }

                        // 3DS handling required
                        else if (conditionNotification?.conditionName === "cardPaymentRequiresAction") {
                            void this.analyticsService.track3DSStart(this.orderTotalPayment());

                            const action: any = conditionNotification.conditionParams["action"];
                            try {
                                const threeDSResult: ThreeDSResult = await this.paymentModel.handle3DS(action, paymentAuthorisation);
                                if (threeDSResult.success) {
                                    retryRequired = true;
                                } else {
                                    console.error(threeDSResult.error);
                                    void this.analyticsService.track3DSFailedOnClient(this.orderTotalPayment());
                                    void this.analyticsService.trackPlaceOrderFailed("paymentAuthorisationFailed");
                                    await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                                    this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                                }
                            } catch (error) {
                                console.error(error);
                                void this.analyticsService.track3DSFailedOnClient(this.orderTotalPayment());
                                void this.analyticsService.trackPlaceOrderFailed("3DS processing error");
                                await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                                this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                            }
                        }

                        // Failure due to outstanding group order participants
                        else if (conditionNotification?.conditionName === "outstandingParticipantRequests") {
                            void this.analyticsService.trackPlaceOrderFailed("outstandingParticipantRequests");
                            await this.notificationService.displayAlert("woa.heading.order.outstandingParticipants", "woa.message.order.outstandingParticipants");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to insufficient integrated loyalty rewards balance
                        else if (conditionNotification?.conditionName === "insufficientIntegratedLoyaltyRewards") {
                            void this.analyticsService.trackPlaceOrderFailed("insufficientIntegratedLoyaltyRewards");
                            await this.notificationService.displayAlert("woa.heading.order.insufficientIntegratedLoyaltyRewards", "woa.message.order.insufficientIntegratedLoyaltyRewards");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to scheduled product unavailability
                        else if (conditionNotification?.conditionName === "notAvailableSchedules") {
                            void this.analyticsService.trackPlaceOrderFailed("notAvailableSchedules");

                            // Attempt to recreate the group order that will have been closed by the failed order placement, rather than confusing the user with a prompt.
                            void this.groupOrderModel.restoreGroupOrder();

                            const orderRequested: Date = conditionNotification.conditionParams["pickupRequested"] ? new Date(conditionNotification.conditionParams["pickupRequested"]) : null;
                            const preparationExpected: Date = EntityData.parseLocalDateTime(conditionNotification.conditionParams["preparationExpected"]);
                            const requestedTime: string = formatTimeForDisplay(orderRequested);
                            const expectedTime: string = formatTimeForDisplay(preparationExpected);
                            const msg: string = this._initiatedOrder().orderLaterDateTime == undefined ?
                                (orderRequested ? "woa.message.order.itemsNotAvailableWhenRequested" : "woa.message.order.itemsNotAvailableNow")
                                : orderRequested && preparationExpected > orderRequested ? "woa.message.order.itemsNotAvailableRevisedSchedule"
                                    : "woa.message.order.itemsNotAvailableActiveSchedule";

                            await this.notificationService.displayAlert("woa.heading.order.itemsNotAvailableActiveSchedule", msg, {
                                requestedTime: requestedTime,
                                expectedTime: expectedTime
                            });
                            this.orderingContextModel.expectedCompletion.set(preparationExpected);
                            await this.orderingContextModel.refreshActiveSchedules("item not available on order placement");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to RSA block
                        else if (conditionNotification?.conditionName === "notAvailableRsaBlock") {
                            void this.analyticsService.trackPlaceOrderFailed("notAvailableRsaBlock");

                            // Attempt to recreate the group order that will have been closed by the failed order placement, rather than confusing the user with a prompt.
                            void this.groupOrderModel.restoreGroupOrder();

                            await this.notificationService.displayAlert("woa.heading.order.itemsRsaBlocked", "woa.message.order.itemsRsaBlocked");
                            this.removalOfOversoldItemsReported.set(true);
                            void this.menuModel.loadMenu("rsa block detected");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Failure due to limited product availability
                        else if (conditionNotification?.conditionName === "productLimitReached") {
                            void this.analyticsService.trackPlaceOrderFailed("productLimitReached");

                            // Attempt to recreate the group order that will have been closed by the failed order placement, rather than confusing the user with a prompt.
                            void this.groupOrderModel.restoreGroupOrder();

                            // const oversoldSellableIds: number[] = conditionNotification.conditionParams["oversoldSellableIds"];

                            await this.notificationService.displayAlert("woa.heading.order.itemsOversold", "woa.message.order.itemsOversold");
                            this.removalOfOversoldItemsReported.set(true);
                            await this.orderingContextModel.refreshActiveSchedules("item limit reached on order placement");
                            void this.menuModel.loadMenu("product limit reached");
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_INVALID);
                        }

                        // Displayable errors
                        else if (displayableMessage) {
                            void this.analyticsService.trackPlaceOrderFailed(displayableMessage.messageKey);
                            if (displayableMessage.messageKey === "server.exception.web-order.intentCreationFailed") {
                                paymentAuthorisationFailed = true;
                                await this.notificationService.displayAlert(undefined, "woa.message.order.paymentAuthorisationFailed");
                            } else if (displayableMessage.messageKey === "server.exception.web-order.intentCreationCardDetailsFailed") {
                                paymentAuthorisationFailed = true;
                                await this.notificationService.displayAlert("woa.message.order.paymentAuthorisationFailed", displayableMessage.messageKey, displayableMessage.messageParams);
                            } else {
                                await this.notificationService.displayAlert("woa.error.order.submitFailed", displayableMessage.messageKey, displayableMessage.messageParams);
                            }
                            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
                        }

                        // Handle all other failure causes
                        else {
                            void this.analyticsService.trackPlaceOrderFailed("submitFailed");
                            if (Date.now() - placementStarted < OrderModel.ORDER_PLACEMENT_AUTO_RETRY_WINDOW_MS ||
                                await this.notificationService.displayConfirmation("woa.heading.order.orderSubmissionFailed", undefined, "woa.error.order.orderSubmissionFailed",
                                    "woa.button.order.cancelOrder", "woa.button.common.retry", false, undefined, true)) {

                                // Retry using the same token (if any), unless payment details have been saved against a new customer,
                                // in which case the previous token may have been assigned to a since-deleted customer.
                                if (paymentAuthorisation.storePaymentDetails) {
                                    paymentAuthorisation.token = undefined;
                                    paymentAuthorisation.isReusable = this.paymentModel.paymentProvider() === IntegratedPaymentProvider.TYRO;
                                }
                                retryRequired = true;
                            } else {
                                this.orderSubmissionStatus.set(OrderSubmissionStatus.ORDER_CANCELLED);
                            }
                        }

                        // Log errors if we are not about to retry
                        if (!retryRequired) {
                            this.notificationService.logErrorToServer("OrderModel.placeOrder - order placement failed", this.orderPlacement(), error);

                            // Track the completion status of the 3DS retry.
                            if (paymentAuthorisation.isThreeDS) {
                                if (paymentAuthorisationFailed) {
                                    void this.analyticsService.track3DSFailedOnServer(this.orderTotalPayment());
                                } else {
                                    // A different server side issue not 3DS related
                                    void this.analyticsService.track3DSSucceeded(this.orderTotalPayment());
                                }
                            }
                        }
                    }

                    // Indicate order placement succeeded
                    return true;
                }, 60000)) {
                    return false;
                }
            }
            return true;
        } catch (error) {
            console.error("placeOrder:", error);
            this.orderSubmissionStatus.set(OrderSubmissionStatus.INCOMPLETE);
            void this.analyticsService.trackAnomaly("placeOrder failed", error);
            return false;
        } finally {
            setTimeout(() => this.orderSubmissionStatus.set(undefined));
        }
    }

    public async getOrderPlacementStatus(keepAsRecentOrder: boolean): Promise<OrderPlacementStatusData> {
        if (!this._placedOrder()) {
            return <OrderPlacementStatusData>EntityData.create(OrderPlacementStatusData);
        }
        const orderPlacementStatus: OrderPlacementStatusData = <OrderPlacementStatusData>EntityData.create(OrderPlacementStatusData)
            .assign(await this.orderPlacementStatusService.performAction({
                requestId: this._placedOrder().requestId,
                lookupCode: this._placedOrder().lookupCode
            }));
        this._placedOrder().status = orderPlacementStatus.status;

        if (keepAsRecentOrder && orderPlacementStatus.status === OrderPlacementStatus.ACCEPTED) {
            this.recentOrderService.addRecentOrder(this.buildRecentOrderData());
            this.recentOrderService.deleteCurrentOrder();
        }
        return orderPlacementStatus;
    }

    public async performPostscript(email?: string, mobilePhone?: string, postCode?: string, marketingOptIn?: boolean): Promise<void> {
        const orderPostscript: OrderPostscriptData = <OrderPostscriptData>EntityData.create(OrderPostscriptData);
        orderPostscript.requestId = this._initiatedOrder().requestId;
        orderPostscript.appUserId = this.appUserService.getAppUserId();
        orderPostscript.email = email;
        orderPostscript.mobilePhone = mobilePhone;
        orderPostscript.postCode = postCode;
        orderPostscript.marketingOptIn = marketingOptIn;
        return this.orderPostscriptService.performAction(orderPostscript);
    }

    /**
     * Sends a list of pending orders to the server and gets back all orders that have actually been placed.
     */
    public async verifyPendingOrders(): Promise<void> {
        if (this.verifyingPendingOrders) {
            return;
        }
        this.verifyingPendingOrders = true;
        this.verifiedOrders = [];
        try {
            const recentOrders: RecentOrderData[] = this.recentOrderService.recentPendingOrders;
            if (recentOrders.length) {
                const pendingOrders: PendingOrdersData = <PendingOrdersData>EntityData.create(PendingOrdersData);
                pendingOrders.pendingOrders = recentOrders.map((recentOrder: RecentOrderData) => {
                    const pendingOrder: PendingOrderData = <PendingOrderData>EntityData.create(PendingOrderData);
                    pendingOrder.requestId = recentOrder.initiatedOrder.requestId;
                    pendingOrder.storeId = recentOrder.initiatedOrder.store.storeId;
                    return pendingOrder;
                });

                const placedOrders: PlacedOrdersData = <PlacedOrdersData>EntityData.create(PlacedOrdersData).assign(await this.pendingOrdersService.performAction(pendingOrders));

                recentOrders.forEach((recentOrder: RecentOrderData) => {
                    const placedOrder: PlacedOrderData = placedOrders.placedOrders.find((value) => value.requestId === recentOrder.initiatedOrder.requestId);
                    if (placedOrder) {
                        recentOrder.placedOrder = placedOrder;
                        recentOrder.pending = false;
                        this.recentOrderService.addRecentOrder(recentOrder);
                        this.verifiedOrders.push(recentOrder);
                    } else {
                        this.recentOrderService.deletePendingOrder(recentOrder.initiatedOrder.requestId);
                    }
                });
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            this.verifyingPendingOrders = false;
        }
    }

    public async notifyVerifiedOrders(): Promise<void> {
        while (this.verifiedOrders.length) {
            const verifiedOrder: RecentOrderData = this.verifiedOrders.shift();
            await this.notificationService.displayAlert("woa.heading.order.orderPlaced", verifiedOrder.orderPlacement.firstName ? "woa.message.order.orderPlaced.withName" : "woa.message.order.orderPlaced.anonymous", {
                name: verifiedOrder.orderPlacement.firstName,
                deliveryIdentifier: verifiedOrder.placedOrder.receiptId
            });
        }
    }
}
